import React, { useContext, useEffect } from "react";
import { Box } from "@mui/material";

import DateArea from "features/metadesk/CommunityAnalysis/components/DateArea";
import { useIntl } from "react-intl";
import EcoScanContext from "features/metadesk/EcoScan/contexts/EcoScan.context";
import { StyledWhiteContainer, StyledTableHeader } from "./EcoScan.styles";
import { map, slice, get } from "lodash-es";
import useEcoScanState from "features/metadesk/EcoScan/hooks/useEcoScanState.hooks";
import {
  AutoCompleteDropdown,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { useTheme } from "@mui/styles";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { sortArrayByKey } from "features/helpers/utils";

const EcoScanContent = ({
  tableHeaders,
  tableContents,
  renderChartArea,
  isOverview,
  type,
}) => {
  const {
    selectedParty,
    setSelectedParty,
    overviewFilterValue,
    setOverviewFilterValue,
    morphTableData,
    setMorphTableData,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    morphTableDataLoading,
  } = useContext(EcoScanContext);

  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { projectPartyList, projectPartyData, projectPartyDataLoading } =
    useEcoScanState();

  const overviewFilterOptions = [
    {
      label: formatMessage({ id: "global.metrics.activeWalletOnChain" }),
      value: "activeWalletOnChain",
    },
    {
      label: formatMessage({ id: "global.metrics.activeWalletOnWebsite" }),
      value: "activeWalletOnWebsite",
    },
    {
      label: formatMessage({ id: "global.metrics.transaction" }),
      value: "transactionCount",
    },
    {
      label: formatMessage({ id: "ecoScan.header.newDiscordMember" }),
      value: "newDiscordMember",
    },
    {
      label: formatMessage({ id: "ecoScan.header.newTelegramMember" }),
      value: "newTelegramMember",
    },
    {
      label: formatMessage({ id: "ecoScan.header.sessionsFootprints" }),
      value: "sessionsFromFootprints",
    },
  ];

  const projectOptions = projectPartyList.map((item) => {
    return {
      label: item.name,
      value: item._id,
    };
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    const newArr = sortArrayByKey(morphTableData, property, newOrder);
    setOrder(newOrder);
    setOrderBy(property);
    setMorphTableData(newArr);
  };

  const handleChangeOverviewFilterValue = (event, value) => {
    setOverviewFilterValue(value.value);
  };

  const handleSelectedParty = (event, value) => {
    const sendValue = value.map((item) => item.value);
    setSelectedParty(sendValue);
  };

  return (
    <Box>
      <DateArea
        sx={{ marginBottom: "8px" }}
        context={EcoScanContext}
        datePickerProps={{
          minDate: new Date().setDate(new Date().getDate() - 30),
          maxDate: new Date(),
        }}
      ></DateArea>
      <>
        <StyledWhiteContainer sx={{ marginBottom: "12px" }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              padding: "16px 8px 16px 16px",
              ...(type !== "overview" && {
                borderBottom: `1px solid ${theme.customColors.grey[300]}`,
              }),
            }}
          >
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#1C1C1E",
                    marginRight: "24px",
                  }}
                >
                  {formatMessage({
                    id: "websiteTraffic.overtime.title",
                  })}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {isOverview && (
                  <Box className="endRow">
                    <Box
                      sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        marginRight: "8px",
                      }}
                      color={theme.customColors.grey[700]}
                    >
                      {formatMessage({
                        id: "ecoScan.overview.overtimeDropdown",
                      })}
                    </Box>
                    <Box width={"360px"}>
                      <AutoCompleteDropdown
                        value={overviewFilterOptions?.find(
                          (option) => option.value === overviewFilterValue
                        )}
                        options={overviewFilterOptions}
                        onChange={handleChangeOverviewFilterValue}
                        size={SIZE.LARGE}
                      />
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <AutoCompleteDropdown
                  multiple={true}
                  placeholder={formatMessage({
                    id: "dropdown.selectEntity.placeholder",
                  })}
                  isCheckbox={true}
                  disableCloseOnSelect={true}
                  value={projectOptions.filter((option) =>
                    selectedParty?.includes(option.value)
                  )}
                  options={projectOptions}
                  onChange={handleSelectedParty}
                  size={SIZE.LARGE}
                  max={5}
                  limitTags={1}
                  customNodeInOption={
                    <Box paddingLeft="16px" mb="8px">
                      <Box
                        color={(theme) => theme.customColors.purple[500]}
                        sx={{
                          fontSize: "10px",
                          marginBottom: "3px",
                          fontWeight: 700,
                        }}
                      >
                        {formatMessage({
                          id: "dropdown.selectEntity.subtitle",
                        })}
                      </Box>
                      <Box
                        color={(theme) => theme.customColors.grey[600]}
                        sx={{ fontSize: "10px", fontWeight: 600 }}
                      >
                        {formatMessage(
                          { id: "dropdown.selectEntity.result" },
                          { n: 5 }
                        )}
                      </Box>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Box padding={"16px"} paddingBottom={"24px"}>
            <Grid container rowSpacing={5}>
              {renderChartArea()}
            </Grid>
          </Box>
        </StyledWhiteContainer>
        <Box>
          <CRMTable
            rowData={morphTableData}
            headers={tableHeaders}
            contents={map(morphTableData, (projectItem, i) =>
              tableContents(projectItem)
            )}
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            isLoading={morphTableDataLoading}
            loadingIndicatorAmount={4}
            // dataCounts={2131}
            // page={1}
            // setPage={() => {}}
          />
        </Box>
      </>
    </Box>
  );
};

export default EcoScanContent;
